"use client";
import { JSX, useMemo, useState } from "react";
import { Speaker, ChevronDown } from "lucide-react";
import { useMediaDeviceSelect } from "@livekit/components-react";

/** Audio Output settings - Allows user to select preferred speaker */
export const AudioOutputSettings = (): JSX.Element => {
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind: "audiooutput" });
  const [isOpen, setIsOpen] = useState(false);

  const activeDeviceLabel = useMemo(() => {
    return devices.find((d) => d.deviceId === activeDeviceId)?.label || "Speaker";
  }, [devices, activeDeviceId]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors focus:outline-none shadow-md shadow-primary/20"
          title={activeDeviceLabel}
        >
          <div className="flex items-center gap-3">
            <Speaker className="w-4 h-4 shrink-0" />
            <span className="truncate max-w-[200px] text-sm font-medium">{activeDeviceLabel}</span>
          </div>
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && devices.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
            <ul className="list-none m-0 p-2 space-y-1">
              {devices.map((device) => (
                <li key={device.deviceId}>
                  <button
                    onClick={() => {
                      setActiveMediaDevice(device.deviceId);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-colors text-sm ${
                      device.deviceId === activeDeviceId
                        ? "bg-blue-50 text-blue-600 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="flex-1 truncate">{device.label}</span>
                    {device.deviceId === activeDeviceId && <span className="text-blue-600">✓</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 px-1">
        Browser security allows changing output device only during active playback.
      </p>
    </div>
  );
};
